/**
 * ConcurrentEditBanner — Day 98.5b (Pre-Launch Polish).
 *
 * Reusable concurrent-edit notice for a single open record. Subscribes to
 * Supabase Realtime postgres_changes (UPDATE, filter id=eq.<recordId>) on the
 * given table — mirroring the SHAPE of AppContext's credit-refresh channel —
 * and, when ANOTHER user updates the record (self-echo guarded via updated_by),
 * shows "{name} updated this {time} ago — refresh to see latest". Refresh calls
 * onRefresh() (the parent's record re-fetch) + clears the banner.
 *
 * Requires the table to be a member of the supabase_realtime publication (Day
 * 98.5b Phase 1 added products + purchase_orders). Phrasing / name-resolution /
 * NULL-fallback logic lives in src/lib/concurrentEdit.ts (pure + unit-tested).
 */

import { useState, useEffect, type JSX } from 'react';
import { RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { concurrentEditMessage } from '../../lib/concurrentEdit';

interface ConcurrentEditBannerProps {
  table: 'products' | 'purchase_orders';
  recordId: string | null | undefined;
  onRefresh: () => void;
}

export default function ConcurrentEditBanner({
  table,
  recordId,
  onRefresh,
}: ConcurrentEditBannerProps): JSX.Element | null {
  const { user } = useApp();
  const [edit, setEdit] = useState<{ fullName: string | null; updatedAt: string } | null>(null);

  useEffect(() => {
    if (!recordId) return undefined;
    setEdit(null); // clear when the viewed record changes
    const channel = supabase
      .channel(`concurrent-edit:${table}:${recordId}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table, filter: `id=eq.${recordId}` },
        (payload) => {
          const next = payload.new as { updated_by?: string | null; updated_at?: string | null };
          // Self-echo guard — never banner the current user's own write.
          if (next.updated_by && user?.id && next.updated_by === user.id) return;
          void (async () => {
            let fullName: string | null = null;
            if (next.updated_by) {
              const { data } = await supabase
                .from('profiles')
                .select('full_name')
                .eq('id', next.updated_by)
                .maybeSingle();
              fullName = (data as { full_name: string | null } | null)?.full_name ?? null;
            }
            setEdit({ fullName, updatedAt: next.updated_at ?? new Date().toISOString() });
          })();
        }
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [table, recordId, user?.id]);

  if (!edit) return null;

  return (
    <div
      role="status"
      className="flex items-center gap-2 px-3 py-2 rounded border border-warning/30 bg-warning/10 text-xs text-ink-primary"
    >
      <span className="flex-1">
        {concurrentEditMessage(edit.fullName, edit.updatedAt, Date.now())}
      </span>
      <button
        type="button"
        onClick={() => {
          setEdit(null);
          onRefresh();
        }}
        className="flex items-center gap-1 px-2 py-1 rounded bg-warning/20 hover:bg-warning/30 text-warning transition-colors cursor-pointer shrink-0"
      >
        <RefreshCw size={12} />
        Refresh
      </button>
    </div>
  );
}
